import * as React from 'react'

import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon } from '@/modules/shared/components'
import { Button } from '@/ui/button'

type Props = {
  title: string
  onClose: () => void
  onConfirm?: () => void
  /** Blokuje przycisk potwierdzenia, np. gdy nic nie wybrano. */
  confirmDisabled?: boolean
  confirmLabel?: string
  width?: number
  /** Domyślnie drukarka (modale wydruków); `null` = pasek tytułu bez ikony. */
  icon?: string | null
  /** Formularze z własnymi przyciskami chowają stopkę modala. */
  hideFooter?: boolean
  children: React.ReactNode
}

/**
 * Okno modalne panelu admina.
 *
 * Pięć modali wydruków miało identyczny szkielet - pasek tytułu z ikoną drukarki,
 * treść i para przycisków - powtórzony w każdym z nich w `FlexView` ze stylami
 * inline. Tutaj jest raz, na `ModalLayout` z nowego frontendu.
 *
 * `icon={null}` + `hideFooter` daje goły modal dla formularzy z własnymi przyciskami
 * (zastąpił klasowy `genericModal` na shimie bootstrapowym).
 */
export const AdminModal: React.FC<Props> = ({
  title,
  onClose,
  onConfirm,
  confirmDisabled,
  confirmLabel,
  width = 720,
  icon = 'print-white',
  hideFooter = false,
  children,
}) => {
  const { t } = useLocalize()

  return (
    <ModalLayout>
      <div
        className="tw-flex tw-max-h-[90vh] tw-w-full tw-flex-col tw-rounded-lg tw-bg-white"
        style={{ maxWidth: width }}
      >
        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-t-lg tw-bg-primary tw-px-4 tw-py-3 tw-text-white">
          {icon && <AdminIcon name={icon} />}
          <span className="tw-truncate tw-text-base">{title}</span>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4">
          {children}
        </div>

        {!hideFooter && (
          <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-base-300 tw-px-4 tw-py-3">
            <Button variant="primary" mode="outline" size="sm" onClick={onClose}>
              {t('operation.cancel')}
            </Button>
            {onConfirm && (
              <Button
                variant="primary"
                size="sm"
                disabled={confirmDisabled}
                onClick={onConfirm}
              >
                {confirmLabel ?? t('answer.ok')}
              </Button>
            )}
          </div>
        )}
      </div>
    </ModalLayout>
  )
}

/** Wiersz „etykieta + kontrolka" - wspólny układ pól w tych modalach. */
export const AdminModalRow: React.FC<{
  label: string
  children: React.ReactNode
}> = ({ label, children }) => (
  <div className="tw-mb-4 tw-flex tw-items-center tw-gap-4">
    <span className="tw-w-40 tw-flex-shrink-0 tw-text-sm tw-text-content-base">
      {label}
    </span>
    <div className="tw-min-w-0 tw-flex-1">{children}</div>
  </div>
)
